import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { ChannelLogo } from '@/components/ChannelLogo';
import { LanguageFlag } from '@/components/LanguageFlag';
import {
  getDefaultInsightContent,
  getDefaultPulseContent,
  type DemoInsightContent,
  type DemoPulseContent,
} from '@/demoContentDefaults';
import {
  MESSAGE_TYPES,
  MORE_CHANNELS,
  MORE_MESSAGE_TYPES,
  PRIMARY_CHANNELS,
  PRIMARY_MESSAGE_TYPES,
  isEmitSupported,
  type DemoChannel,
  type DemoLanguage,
  type DemoMessageType,
  type SalespersonOption,
} from '@/types/demo';

const LANGUAGES: { id: DemoLanguage; label: string }[] = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' },
];

export function ConsolePage() {
  const { session, logout, isLoggingOut, expireSession } = useAuth();
  const token = session?.bearerToken ?? null;
  const [salespeople, setSalespeople] = useState<SalespersonOption[]>([]);
  const [salespersonId, setSalespersonId] = useState('');
  const [channel, setChannel] = useState<DemoChannel>('slack');
  const [messageType, setMessageType] = useState<DemoMessageType>('insight');
  const [language, setLanguage] = useState<DemoLanguage>('es');
  const [pulseContent, setPulseContent] = useState<DemoPulseContent>(() =>
    getDefaultPulseContent('es')
  );
  const [insightContent, setInsightContent] = useState<DemoInsightContent>(() =>
    getDefaultInsightContent('es')
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emitError, setEmitError] = useState<string | null>(null);
  const [emitOk, setEmitOk] = useState<string | null>(null);
  const [isEmitting, setIsEmitting] = useState(false);
  const [isChannelMoreOpen, setIsChannelMoreOpen] = useState(false);
  const [isMessageMoreOpen, setIsMessageMoreOpen] = useState(false);
  const channelMoreMenuRef = useRef<HTMLDivElement>(null);
  const messageMoreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPulseContent(getDefaultPulseContent(language));
    setInsightContent(getDefaultInsightContent(language));
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        return;
      }
      setLoadError(null);
      try {
        const response = await fetch('/api/salespeople', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await response.json()) as {
          salespeople?: SalespersonOption[];
          error?: string;
        };
        if (response.status === 401) {
          // Do not force logout on API 401 — wrong JWT secret used to clear a valid login.
          setLoadError(
            data.error ??
              'API unauthorized. Check AUTH_TOKEN_PRIVATE_KEY or restart the demo API.'
          );
          return;
        }
        if (!response.ok) {
          throw new Error(data.error ?? 'Failed to load salespeople');
        }
        if (!cancelled) {
          const list = data.salespeople ?? [];
          setSalespeople(list);
          if (list[0]) {
            setSalespersonId(list[0].id);
          }
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Load failed');
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (isEmitSupported(channel, messageType)) {
      return;
    }
    const fallback = MESSAGE_TYPES.find((item) => isEmitSupported(channel, item.id));
    if (fallback) {
      setMessageType(fallback.id);
    }
  }, [channel, messageType]);

  const selected = useMemo(
    () => salespeople.find((person) => person.id === salespersonId) ?? null,
    [salespeople, salespersonId]
  );

  const selectedDestination = selected?.channels?.[channel] ?? null;
  const isMoreChannel = MORE_CHANNELS.some((item) => item.id === channel);
  const selectedMoreMessage = MORE_MESSAGE_TYPES.find((item) => item.id === messageType) ?? null;

  const channelAvailable = useCallback(
    (ch: DemoChannel) => Boolean(selected?.channels?.[ch]?.value),
    [selected]
  );

  const comboOk = isEmitSupported(channel, messageType) && channelAvailable(channel);

  useEffect(() => {
    if (!isChannelMoreOpen && !isMessageMoreOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        isChannelMoreOpen &&
        channelMoreMenuRef.current &&
        !channelMoreMenuRef.current.contains(target)
      ) {
        setIsChannelMoreOpen(false);
      }
      if (
        isMessageMoreOpen &&
        messageMoreMenuRef.current &&
        !messageMoreMenuRef.current.contains(target)
      ) {
        setIsMessageMoreOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChannelMoreOpen(false);
        setIsMessageMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isChannelMoreOpen, isMessageMoreOpen]);

  const onSelectChannel = useCallback((next: DemoChannel) => {
    setChannel(next);
    setIsChannelMoreOpen(false);
  }, []);

  const onSelectMessageType = useCallback((next: DemoMessageType) => {
    setMessageType(next);
    setIsMessageMoreOpen(false);
  }, []);

  const onSelectLanguage = useCallback((next: DemoLanguage) => {
    setLanguage(next);
  }, []);

  const onPulseQuestionChange = useCallback((value: string) => {
    setPulseContent((prev) => ({ ...prev, question: value }));
  }, []);

  const onPulseAnswerChange = useCallback((index: number, value: string) => {
    setPulseContent((prev) => {
      const answers = [...prev.answers] as DemoPulseContent['answers'];
      answers[index] = value;
      return { ...prev, answers };
    });
  }, []);

  const onInsightFieldChange = useCallback(
    (field: keyof DemoInsightContent, value: string) => {
      setInsightContent((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onEmit = useCallback(async () => {
    setEmitError(null);
    setEmitOk(null);
    if (!comboOk || !token) {
      setEmitError('Combinación no soportada o canal sin destino para este comercial.');
      return;
    }
    setIsEmitting(true);
    try {
      const content =
        messageType === 'pulse'
          ? { pulse: pulseContent }
          : messageType === 'insight'
            ? { insight: insightContent }
            : undefined;
      const response = await fetch('/api/emit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          salespersonId,
          channel,
          messageType,
          language,
          ...(content ? { content } : {}),
        }),
      });
      if (response.status === 401) {
        expireSession();
        return;
      }
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        requestId?: string;
        error?: string;
        details?: unknown;
      };
      if (!response.ok) {
        setEmitError(data.error ?? `Emit failed (${response.status})`);
        return;
      }
      setEmitOk(`Enviado (${data.requestId ?? 'ok'}). Revisa el canal.`);
    } catch {
      setEmitError('No se pudo contactar con la API.');
    } finally {
      setIsEmitting(false);
    }
  }, [
    channel,
    comboOk,
    expireSession,
    insightContent,
    language,
    messageType,
    pulseContent,
    salespersonId,
    token,
  ]);

  const onLogout = useCallback(() => {
    void logout();
  }, [logout]);

  return (
    <div data-testid="demo-console" className="mx-auto w-full max-w-2xl flex-1 px-4 py-10 sm:px-6">
      <header
        data-testid="demo-console-header"
        className="mb-10 flex items-start justify-between gap-4"
      >
        <div>
          <p className="font-body text-xs font-semibold uppercase tracking-[0.16em] text-indigo-600">
            Kincode AI
          </p>
          <h1 className="type-h1 mt-1">Demo console</h1>
          <p className="type-body mt-1">
            company {session?.companyID} · employee {session?.employeeID}
          </p>
        </div>
        <button
          type="button"
          data-testid="demo-console-logout"
          onClick={onLogout}
          disabled={isLoggingOut}
          className="font-body text-sm font-medium text-slate-500 underline-offset-2 hover:underline disabled:opacity-50 dark:text-slate-400"
        >
          {isLoggingOut ? 'Saliendo…' : 'Salir'}
        </button>
      </header>

      <section
        data-testid="demo-console-form"
        className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900"
      >
        {loadError ? (
          <p className="font-body text-sm text-red-700 dark:text-red-400" role="alert">
            {loadError}
          </p>
        ) : null}

        <label className="block space-y-1.5">
          <span className="type-label">Comercial</span>
          <select
            data-testid="demo-console-salesperson"
            value={salespersonId}
            onChange={(e) => setSalespersonId(e.target.value)}
            className="demo-input"
          >
            {salespeople.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="space-y-2">
          <legend className="type-label flex min-w-0 max-w-full items-baseline gap-1.5">
            <span>Canal</span>
            <span
              data-testid="demo-console-destination"
              className="min-w-0 truncate font-mono text-[11px] font-normal normal-case tracking-normal text-slate-500 dark:text-slate-400"
              title={
                selectedDestination
                  ? `${selectedDestination.kindLabel}: ${selectedDestination.value}`
                  : 'Sin destino configurado'
              }
            >
              {selectedDestination
                ? `(${selectedDestination.value})`
                : '(sin destino)'}
            </span>
          </legend>
          <div className="flex items-stretch gap-2">
            <div className="grid min-w-0 flex-1 grid-cols-3 gap-2">
              {PRIMARY_CHANNELS.map((item) => {
                const enabled = channelAvailable(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`demo-console-channel-${item.id}`}
                    disabled={!enabled}
                    onClick={() => onSelectChannel(item.id)}
                    className={`demo-btn-choice ${
                      channel === item.id ? 'demo-btn-choice-active' : ''
                    } disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    <ChannelLogo channel={item.id} className="h-5 w-5 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div ref={channelMoreMenuRef} className="relative shrink-0">
              <button
                type="button"
                data-testid="demo-console-channel-more"
                aria-haspopup="menu"
                aria-expanded={isChannelMoreOpen}
                aria-label="Más canales"
                onClick={() => {
                  setIsMessageMoreOpen(false);
                  setIsChannelMoreOpen((open) => !open);
                }}
                className={`demo-btn-choice h-full min-w-[2.75rem] px-2 ${
                  isMoreChannel || isChannelMoreOpen ? 'demo-btn-choice-active' : ''
                }`}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="5" r="1.75" />
                  <circle cx="12" cy="12" r="1.75" />
                  <circle cx="12" cy="19" r="1.75" />
                </svg>
              </button>

              {isChannelMoreOpen ? (
                <div
                  role="menu"
                  data-testid="demo-console-channel-more-menu"
                  className="absolute right-0 z-20 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                  {MORE_CHANNELS.map((item) => {
                    const enabled = channelAvailable(item.id);
                    const active = channel === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        data-testid={`demo-console-channel-${item.id}`}
                        disabled={!enabled}
                        onClick={() => onSelectChannel(item.id)}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left font-body text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                          active
                            ? 'bg-indigo-50 text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-100'
                            : 'text-slate-900 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <ChannelLogo channel={item.id} className="h-5 w-5 shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="type-label flex min-w-0 max-w-full items-baseline gap-1.5">
            <span>Mensaje</span>
            {selectedMoreMessage ? (
              <span className="min-w-0 truncate font-normal normal-case tracking-normal text-slate-500 dark:text-slate-400">
                ({selectedMoreMessage.label})
              </span>
            ) : null}
          </legend>
          <div className="flex items-stretch gap-2">
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
              {PRIMARY_MESSAGE_TYPES.map((item) => {
                const enabled = isEmitSupported(channel, item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`demo-console-message-${item.id}`}
                    disabled={!enabled}
                    onClick={() => onSelectMessageType(item.id)}
                    className={`demo-btn-choice ${
                      messageType === item.id ? 'demo-btn-choice-active' : ''
                    } disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div ref={messageMoreMenuRef} className="relative shrink-0">
              <button
                type="button"
                data-testid="demo-console-message-more"
                aria-haspopup="menu"
                aria-expanded={isMessageMoreOpen}
                aria-label="Más mensajes"
                onClick={() => {
                  setIsChannelMoreOpen(false);
                  setIsMessageMoreOpen((open) => !open);
                }}
                className={`demo-btn-choice h-full min-w-[2.75rem] px-2 ${
                  selectedMoreMessage || isMessageMoreOpen ? 'demo-btn-choice-active' : ''
                }`}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="5" r="1.75" />
                  <circle cx="12" cy="12" r="1.75" />
                  <circle cx="12" cy="19" r="1.75" />
                </svg>
              </button>

              {isMessageMoreOpen ? (
                <div
                  role="menu"
                  data-testid="demo-console-message-more-menu"
                  className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                  {MORE_MESSAGE_TYPES.map((item) => {
                    const enabled = isEmitSupported(channel, item.id);
                    const active = messageType === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        data-testid={`demo-console-message-${item.id}`}
                        disabled={!enabled}
                        onClick={() => onSelectMessageType(item.id)}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left font-body text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                          active
                            ? 'bg-indigo-50 text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-100'
                            : 'text-slate-900 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
          {!isEmitSupported(channel, messageType) ? (
            <p className="font-body text-xs text-slate-500 dark:text-slate-400">
              Este canal no soporta ese tipo de mensaje todavía.
            </p>
          ) : null}
        </fieldset>

        {messageType === 'pulse' ? (
          <fieldset
            data-testid="demo-console-pulse-content"
            className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/40"
          >
            <legend className="type-label px-1">Contenido del pulso</legend>
            <label className="block space-y-1.5">
              <span className="type-label">Pregunta</span>
              <input
                data-testid="demo-console-pulse-question"
                type="text"
                value={pulseContent.question}
                onChange={(e) => onPulseQuestionChange(e.target.value)}
                className="demo-input"
              />
            </label>
            <div className="space-y-2">
              <span className="type-label">Respuestas (5)</span>
              {pulseContent.answers.map((answer, index) => (
                <input
                  key={`pulse-answer-${index}`}
                  data-testid={`demo-console-pulse-answer-${index}`}
                  type="text"
                  value={answer}
                  onChange={(e) => onPulseAnswerChange(index, e.target.value)}
                  className="demo-input"
                />
              ))}
            </div>
          </fieldset>
        ) : null}

        {messageType === 'insight' ? (
          <fieldset
            data-testid="demo-console-insight-content"
            className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/40"
          >
            <legend className="type-label px-1">Contenido del insight</legend>
            <label className="block space-y-1.5">
              <span className="type-label">Driver</span>
              <input
                data-testid="demo-console-insight-driver"
                type="text"
                value={insightContent.driver}
                onChange={(e) => onInsightFieldChange('driver', e.target.value)}
                className="demo-input"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="type-label">Pregunta afectada</span>
              <input
                data-testid="demo-console-insight-question"
                type="text"
                value={insightContent.question}
                onChange={(e) => onInsightFieldChange('question', e.target.value)}
                className="demo-input"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="type-label">Training</span>
              <input
                data-testid="demo-console-insight-training"
                type="text"
                value={insightContent.training}
                onChange={(e) => onInsightFieldChange('training', e.target.value)}
                className="demo-input"
              />
            </label>
          </fieldset>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="type-label">Idioma</legend>
          <div className="grid grid-cols-2 gap-2">
            {LANGUAGES.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`demo-console-language-${item.id}`}
                onClick={() => onSelectLanguage(item.id)}
                className={`demo-btn-choice ${
                  language === item.id ? 'demo-btn-choice-active' : ''
                }`}
              >
                <LanguageFlag
                  language={item.id}
                  className="h-4 w-6 shrink-0 rounded-[3px] shadow-sm ring-1 ring-black/10"
                />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <button
          type="button"
          data-testid="demo-console-emit"
          disabled={!comboOk || isEmitting}
          onClick={() => void onEmit()}
          className="demo-btn-primary"
        >
          {isEmitting ? 'Enviando…' : 'Emitir mensaje'}
        </button>

        {emitError ? (
          <p
            data-testid="demo-console-error"
            className="font-body text-sm text-red-700 dark:text-red-400"
            role="alert"
          >
            {emitError}
          </p>
        ) : null}
        {emitOk ? (
          <p
            data-testid="demo-console-success"
            className="font-body text-sm font-medium text-indigo-600"
            role="status"
          >
            {emitOk}
          </p>
        ) : null}
      </section>
    </div>
  );
}
